import {
  AfterViewInit,
  Directive,
  ElementRef,
  Inject,
  NgZone,
  OnDestroy,
  PLATFORM_ID,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';

@Directive({
  selector: '[bfParallax]',
  standalone: true,
})
export class ParallaxDirective implements AfterViewInit, OnDestroy {
  private readonly isBrowser: boolean;

  private itemEl!: HTMLElement;
  private hostEl: HTMLElement | null = null;
  private mediaWrapperEl: HTMLElement | null = null;
  private mediaEl: HTMLElement | null = null;

  private animationFrame: number | null = null;
  private resizeFrame: number | null = null;

  private scrollListener?: () => void;
  private resizeListener?: () => void;

  private active = false;

  constructor(
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly zone: NgZone,
    @Inject(PLATFORM_ID) platformId: object,
  ) {
    this.isBrowser = isPlatformBrowser(platformId);
  }

  ngAfterViewInit(): void {
    if (!this.isBrowser) {
      return;
    }

    if (
      window.matchMedia(
        '(prefers-reduced-motion: reduce)',
      ).matches
    ) {
      return;
    }

    this.itemEl = this.elementRef.nativeElement;

    this.hostEl =
      this.itemEl.closest<HTMLElement>(
        '[data-parallax-host]',
      );

    this.mediaWrapperEl =
      this.itemEl.querySelector<HTMLElement>(
        '[data-parallax-image-wrapper]',
      );

    if (!this.hostEl || !this.mediaWrapperEl) {
      return;
    }

    this.mediaEl =
      this.mediaWrapperEl.querySelector<HTMLElement>('img');

    this.active = true;

    this.prepareImage();

    this.zone.runOutsideAngular(() => {
      this.scrollListener = () => {
        this.requestUpdate();
      };

      this.resizeListener = () => {
        this.requestResize();
      };

      window.addEventListener(
        'scroll',
        this.scrollListener,
        { passive: true },
      );

      window.addEventListener(
        'resize',
        this.resizeListener,
        { passive: true },
      );

      this.refreshGeometry();
      this.requestUpdate();
    });
  }

  private prepareImage(): void {
    if (!this.mediaEl) {
      return;
    }

    /*
     * Source loads the wrapper's image via
     * ImageLoader.load(el, { mode: "fill" }), Squarespace's own
     * cover-fit implementation: the image always fills the wrapper
     * box in both dimensions, cropping the overflowing axis. A
     * naive height:100%/width:auto only covers when the image's
     * aspect ratio is wider than the wrapper's -- once the hero
     * gets short and wide (intrinsic height, full-bleed width) that
     * assumption breaks and the item's black background shows
     * through at the sides. object-fit: cover reproduces the real
     * fill-mode behavior unconditionally.
     */
    this.mediaEl.style.display = 'block';
    this.mediaEl.style.width = '100%';
    this.mediaEl.style.height = '100%';
    this.mediaEl.style.maxWidth = 'none';
    this.mediaEl.style.objectFit = 'cover';
    this.mediaEl.style.objectPosition = '50% 50%';
    this.mediaEl.style.position = '';
    this.mediaEl.style.left = '';
    this.mediaEl.style.transform = '';
  }

  private requestResize(): void {
    if (this.resizeFrame !== null) {
      return;
    }

    this.resizeFrame =
      window.requestAnimationFrame(() => {
        this.resizeFrame = null;

        /*
         * Browser has now applied the new viewport dimensions.
         * Recalculate the section geometry just like the source.
         */
        this.refreshGeometry();
        this.requestUpdate();
      });
  }

  private requestUpdate(): void {
    if (this.animationFrame !== null) {
      return;
    }

    this.animationFrame =
      window.requestAnimationFrame(() => {
        this.animationFrame = null;

        this.update();
      });
  }

  private refreshGeometry(): void {
    if (
      !this.active ||
      !this.hostEl ||
      !this.mediaWrapperEl
    ) {
      return;
    }

    const rect =
      this.hostEl.getBoundingClientRect();

    const scrollX =
      window.scrollX ||
      window.pageXOffset ||
      0;

    const scrollY =
      window.scrollY ||
      window.pageYOffset ||
      0;

    const documentTop =
      rect.top + scrollY;

    const documentLeft =
      rect.left + scrollX;

    const sectionWidth =
      rect.width;

    const sectionHeight =
      rect.height;

    /*
     * Source:
     *
     * parallaxItem.style.top = top
     * parallaxItem.style.left = left
     * parallaxItem.style.width = width
     * parallaxItem.style.height = height
     */
    this.itemEl.style.position = 'fixed';

    this.itemEl.style.top =
      `${documentTop}px`;

    this.itemEl.style.left =
      `${documentLeft}px`;

    this.itemEl.style.width =
      `${sectionWidth}px`;

    this.itemEl.style.height =
      `${sectionHeight}px`;

    /*
     * Source new math:
     *
     * wrapper.bottom =
     *   -0.5 * (window.innerHeight - sectionHeight)
     */
    const bottom =
      -0.5 *
      (window.innerHeight - sectionHeight);

    this.mediaWrapperEl.style.top = '';

    this.mediaWrapperEl.style.bottom =
      `${bottom}px`;

    this.mediaWrapperEl.style.willChange =
      'transform';

    this.itemEl.style.willChange =
      'transform';

    /*
     * Make sure the source-like image geometry is still
     * active after resize.
     */
    this.prepareImage();
  }

  private update(): void {
    if (
      !this.active ||
      !this.hostEl ||
      !this.mediaWrapperEl
    ) {
      return;
    }

    const scrollTop =
      document.documentElement.scrollTop ||
      document.body.scrollTop ||
      window.scrollY ||
      0;

    const scrollLeft =
      window.scrollX ||
      window.pageXOffset ||
      0;

    const viewportHeight =
      window.innerHeight;

    const rect =
      this.hostEl.getBoundingClientRect();

    const documentTop =
      rect.top + scrollTop;

    const documentLeft =
      rect.left + scrollLeft;

    const sectionWidth =
      rect.width;

    const sectionHeight =
      rect.height;

    const documentBottom =
      documentTop + sectionHeight;

    /*
     * Source visibility check.
     */
    const isVisible =
      scrollTop + viewportHeight > documentTop &&
      scrollTop < documentBottom;

    if (!isVisible) {
      this.itemEl.style.transform =
        `translate3d(${-(sectionWidth + documentLeft)}px, 0, 0)`;

      return;
    }

    /*
     * Source fixed positioning:
     *
     * translate3d(0, -scrollTop, 0)
     */
    this.itemEl.style.transform =
      `translate3d(0, ${-scrollTop}px, 0)`;

    /*
     * Source new-math:
     *
     * d = -0.5 * (sectionTop - scrollTop)
     */
    const translateY =
      -0.5 *
      (documentTop - scrollTop);

    this.mediaWrapperEl.style.transform =
      `translate3d(0, ${translateY}px, 0)`;
  }

  ngOnDestroy(): void {
    if (!this.isBrowser) {
      return;
    }

    if (this.scrollListener) {
      window.removeEventListener(
        'scroll',
        this.scrollListener,
      );
    }

    if (this.resizeListener) {
      window.removeEventListener(
        'resize',
        this.resizeListener,
      );
    }

    if (this.animationFrame !== null) {
      window.cancelAnimationFrame(
        this.animationFrame,
      );

      this.animationFrame = null;
    }

    if (this.resizeFrame !== null) {
      window.cancelAnimationFrame(
        this.resizeFrame,
      );

      this.resizeFrame = null;
    }
  }
}
